export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000';

const isBrowser = typeof window !== 'undefined';
const normalizeSlug = (value) => (value || '').toString().trim().toLowerCase();
const RESERVED_ROUTE_PREFIXES = new Set([
  'store',
  'offsite',
  'portal',
  'confirmation',
  'checkout',
  'terms-and-conditions',
]);

const getSlugFromPath = () => {
  if (!isBrowser) return '';
  const segments = window.location.pathname.split('/').filter(Boolean).map(normalizeSlug);
  if (!segments.length) return '';
  if (RESERVED_ROUTE_PREFIXES.has(segments[0])) {
    return '';
  }
  if (segments[1] && RESERVED_ROUTE_PREFIXES.has(segments[1])) {
    return segments[0];
  }
  return segments[0];
};

const getSlugFromQuery = () => {
  if (!isBrowser) return '';
  const params = new URLSearchParams(window.location.search);
  return params.get('tenantSlug') || params.get('tenant') || '';
};

const getSlugFromHostname = () => {
  if (!isBrowser) return '';
  const host = window.location.hostname || '';
  if (!host) return '';
  if (/^(localhost|127(?:\.0\.0\.1)?)/.test(host)) {
    return '';
  }
  const parts = host.split('.');
  if (parts.length <= 2) return parts[0] || '';
  return parts[0];
};

const DEFAULT_TENANT_SLUG =
  normalizeSlug(import.meta.env.VITE_DEFAULT_TENANT_SLUG) || 'build-a-bear';
const TENANT_SLUG =
  normalizeSlug(import.meta.env.VITE_TENANT_SLUG) ||
  normalizeSlug(import.meta.env.VITE_TENANT) ||
  normalizeSlug(getSlugFromPath()) ||
  normalizeSlug(getSlugFromQuery()) ||
  normalizeSlug(getSlugFromHostname()) ||
  DEFAULT_TENANT_SLUG;

export const tenantHeaders = TENANT_SLUG
  ? {
      'X-Tenant-Slug': TENANT_SLUG,
      'X-Tenant': TENANT_SLUG,
    }
  : {};

export const apiRequest = async ({ path, method = 'GET', body, headers = {}, timeoutMs = 15000 }) => {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timeoutId = controller ? window.setTimeout(() => controller.abort(), timeoutMs) : null;

  let response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...tenantHeaders,
        ...headers,
      },
      body: body ? JSON.stringify(body) : undefined,
      signal: controller?.signal,
    });
  } catch (error) {
    if (timeoutId) window.clearTimeout(timeoutId);
    if (error?.name === 'AbortError') {
      throw new Error('Request timed out. Please try again.');
    }
    throw error;
  }

  if (timeoutId) window.clearTimeout(timeoutId);

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const error = new Error(data?.message || 'Request failed');
    error.status = response.status;
    throw error;
  }

  if (response.status === 204) {
    return null;
  }

  return response.json();
};
